import React, { useMemo, useState } from "react";
import { FixedSizeGrid } from "react-window";

const COLUMN_WIDTH = 200;
const ROW_HEIGHT = 50;
const GRID_HEIGHT = 480;
const HEADER_HEIGHT = 40;
const DATA_GRID_HEIGHT = GRID_HEIGHT - HEADER_HEIGHT;

function sortDataset(dataset, sortBy, sortDirection) {
  return [...dataset].sort((a, b) => {
    const cmp = String(a[sortBy] ?? "").localeCompare(
      String(b[sortBy] ?? ""),
      undefined,
      { numeric: true }
    );
    return sortDirection === "DESC" ? -cmp : cmp;
  });
}

export default function Virtualized({ dataset }) {
  const columns = useMemo(
    () => (dataset?.length ? Object.keys(dataset[0]) : []),
    [dataset]
  );

  const [sortBy, setSortBy] = useState(() => columns[0] ?? "");
  const [sortDirection, setSortDirection] = useState("ASC");

  const effectiveSortBy = columns.includes(sortBy) ? sortBy : columns[0] ?? "";

  const sortedList = useMemo(
    () =>
      dataset?.length
        ? sortDataset(dataset, effectiveSortBy, sortDirection)
        : [],
    [dataset, effectiveSortBy, sortDirection]
  );

  if (!dataset?.length || !columns.length) {
    return null;
  }

  const handleHeaderClick = (column) => {
    if (column === effectiveSortBy) {
      setSortDirection((direction) => (direction === "ASC" ? "DESC" : "ASC"));
      return;
    }

    setSortBy(column);
    setSortDirection("ASC");
  };

  const tableWidth = columns.length * COLUMN_WIDTH;

  return (
    <div style={{ height: GRID_HEIGHT, width: tableWidth }}>
      <div
        className="flex border-b border-un-blue font-semibold"
        style={{ height: HEADER_HEIGHT, width: tableWidth }}
      >
        {columns.map((column) => (
          <div
            key={column}
            style={{ width: COLUMN_WIDTH }}
            className="flex items-center min-w-0 overflow-hidden cursor-pointer px-2"
            onClick={() => handleHeaderClick(column)}
          >
            <span className="truncate">{column.toUpperCase()}</span>
          </div>
        ))}
      </div>

      <FixedSizeGrid
        key={columns.length}
        columnCount={columns.length}
        columnWidth={COLUMN_WIDTH}
        rowCount={sortedList.length}
        rowHeight={ROW_HEIGHT}
        height={DATA_GRID_HEIGHT}
        width={tableWidth}
        style={{ overflowX: "hidden" }}
      >
        {({ columnIndex, rowIndex, style }) => {
          const column = columns[columnIndex];
          const value = sortedList[rowIndex][column];

          return (
            <div
              style={style}
              className="flex items-start min-w-0 overflow-hidden px-2 pt-2 border-b border-gray-100"
            >
              <span className="truncate">{value ?? ""}</span>
            </div>
          );
        }}
      </FixedSizeGrid>
    </div>
  );
}
